// src/components/SessionEngagementTracker.tsx

"use client"

import { useEffect, type ReactNode } from "react"

import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  hasExplicitAnalyticsOptOut,
  isAnalyticsStorageAllowed,
} from "~/lib/analytics/consent"
import {
  bindReloadPagehideDetection,
  hasSessionEngagementBeenEmitted,
  isReloadPagehideUnload,
  markSessionEngagementDeferredForReload,
  prepareSessionEngagementForDocumentEntry,
  recordActiveTime,
  registerActiveTimeFlusher,
  updateSessionMaxScroll,
} from "~/lib/site-intelligence/session-engagement"
import { trackSessionEngagement } from "~/lib/gtag"
import { throttle } from "~/lib/throttle"

/**
 * Accumulates visible-tab active time for the tab session and emits exactly one
 * terminal `session_engagement` summary on `pagehide` (tab close / hard nav away).
 *
 * Tab scope is the browser tab — not tied to pathname — so SPA route changes do
 * not reset timers or duplicate terminal events. Reload document entry clears
 * the emitted marker only when the prior pagehide deferred for a detected
 * reload; undetected toolbar reloads that already emitted keep the marker.
 */
const SessionEngagementTracker = (): ReactNode => {
  useEffect(() => {
    if (typeof window === "undefined") return

    prepareSessionEngagementForDocumentEntry()
    const unbindReloadDetection = bindReloadPagehideDetection()

    let visibleSince = Date.now()

    const resetActiveTimer = (): void => {
      visibleSince = Date.now()
    }

    const recordVisibleElapsed = (): void => {
      const now = Date.now()
      const delta = now - visibleSince
      if (delta > 0) {
        recordActiveTime(delta)
      }
      visibleSince = now
    }

    const flushActiveTime = (): void => {
      if (document.visibilityState !== "visible") return
      recordVisibleElapsed()
    }

    const onScroll = throttle((): void => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - doc.clientHeight
      if (scrollable <= 0) {
        return
      }
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
      updateSessionMaxScroll(pct)
    }, 200)

    const unregisterFlusher = registerActiveTimeFlusher(flushActiveTime)

    const emitSessionOnce = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        recordVisibleElapsed()
        return
      }
      // Reload: counters persist in sessionStorage; defer terminal summary to
      // tab close so the same cumulative window is not emitted twice.
      if (isReloadPagehideUnload()) {
        recordVisibleElapsed()
        markSessionEngagementDeferredForReload()
        return
      }
      if (hasSessionEngagementBeenEmitted()) return
      if (document.visibilityState === "visible") {
        recordVisibleElapsed()
      }
      trackSessionEngagement(window.location.pathname, { beacon: true })
    }

    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return
      resetActiveTimer()
    }

    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        recordVisibleElapsed()
      } else {
        resetActiveTimer()
      }
    }

    const onConsentChange = (): void => {
      if (hasExplicitAnalyticsOptOut() || !isAnalyticsStorageAllowed()) {
        resetActiveTimer()
        return
      }
      resetActiveTimer()
    }

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        flushActiveTime()
      }
    }, 15_000)

    window.addEventListener("scroll", onScroll, { passive: true })
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pagehide", emitSessionOnce)
    window.addEventListener("pageshow", onPageShow)
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key === null || event.key === CONSENT_STORAGE_KEY) {
        onConsentChange()
      }
    })

    return () => {
      flushActiveTime()
      unbindReloadDetection()
      unregisterFlusher()
      window.clearInterval(interval)
      onScroll.cancel()
      window.removeEventListener("scroll", onScroll)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pagehide", emitSessionOnce)
      window.removeEventListener("pageshow", onPageShow)
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChange)
    }
  }, [])

  return null
}

export default SessionEngagementTracker
